import { differenceInDays } from "date-fns";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { dateFrom, now } from "@/services/fp/date";

const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

const REPAIR_STATE_LABELS: Record<string, string> = {
	waiting_attribution: "Espera atribuição",
	waiting_repair: "Espera reparação",
	in_repair: "Em reparação",
	waiting_quotation_approve: "Espera aprovação do orçamento",
	waiting_material: "Espera material",
	waiting_delivery: "Espera entrega",
	closed_without_permission: "Fechado sem permissão",
	delivered: "Entregue",
	waiting_avaluation: "Espera avaliação",
	in_avaluation: "Em avaliação",
	contact_client: "Contactar cliente",
	paused: "Em pausa",
	waiting_complaint_avaluation: "Avaliar responsabilidade",
	in_tests: "Em testes",
	problematic: "Problemático",
	waiting_answer: "Espera resposta",
	in_transit: "Em transito",
	waiting_warranty_dispatch: "Espera garantia",
	in_warranty: "Na garantia",
};

type EquipmentSummary = {
	brand?: string | null;
	model?: string | null;
};

type ClientSummary = {
	name?: string | null;
	isStore?: boolean | null;
};

type TechnicianSummary = {
	name?: string | null;
};

type RepairEntry = {
	id?: string | number | null;
	repairNumber?: string | number | null;
	storeRepairIdentifier?: string | null;
	storeBox?: string | null;
	clientEquipment?: EquipmentSummary | null;
	client?: ClientSummary | null;
	quotation?: number | null;
	problemName?: string | null;
	problemDescription?: string | null;
	state?: string | null;
	createdAt?: string | Date | null;
	quotationApproved?: boolean | null;
	complaint?: boolean | null;
	technician?: TechnicianSummary | null;
};

type RepairsTableProps = {
	repairs: RepairEntry[];
	loading?: boolean;
	onView: (repair: RepairEntry) => void;
	onChangeTechnician: (repair: RepairEntry) => void;
	onClientClick?: (client: ClientSummary) => void;
	showState?: boolean;
};

const truncateText = (value: string, maxLength: number) =>
	value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;

const buildEquipmentLabel = (equipment?: EquipmentSummary | null) => {
	if (!equipment) {
		return "-";
	}

	const brand = equipment.brand?.trim() ?? "";
	const model = equipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();
	return label.length > 0 ? label : "-";
};

const buildClientLabel = (client?: ClientSummary | null) => {
	if (!client?.name) {
		return "-";
	}

	const name = client.name.trim();
	return client.isStore ? truncateText(name, 20) : name;
};

const resolveProblemLabel = (repair: RepairEntry) => {
	if (repair.problemName === "Outro") {
		return repair.problemDescription ?? "-";
	}

	return repair.problemName ?? "-";
};

const formatDuration = (value?: string | Date | null) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	const days = differenceInDays(now(), date);
	return days !== 0 ? `${days} dias` : dateFrom(date);
};

const getRepairStateLabel = (state?: string | null) =>
	state ? (REPAIR_STATE_LABELS[state] ?? "Invalid") : "";

const canChangeTechnician = (state?: string | null) => {
	const validStates = [
		"waiting_atribution",
		"problematic",
		"in_tests",
		"paused",
		"waiting_repair",
		"in_repair",
	];

	return state ? validStates.includes(state) : false;
};

export function RepairsTable({
	repairs,
	loading = false,
	onView,
	onChangeTechnician,
	onClientClick,
	showState = true,
}: RepairsTableProps) {
	const columnCount = showState ? 10 : 9;
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`repairs-skeleton-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-10 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/40" />
					</TableCell>
					{showState ? (
						<TableCell>
							<div className="h-4 w-20 rounded bg-muted/40" />
						</TableCell>
					) : null}
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-32 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/40" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={columnCount}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem reparações.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[80px]">#</TableHead>
						<TableHead className="w-[100px] text-center">F. Loja</TableHead>
						<TableHead className="w-[70px] text-center">Cx.</TableHead>
						<TableHead className="w-[160px]">Modelo</TableHead>
						<TableHead className="w-[200px]">Cliente</TableHead>
						{showState ? (
							<TableHead className="w-[150px]">Estado</TableHead>
						) : null}
						<TableHead className="text-center">Avaria</TableHead>

						<TableHead className="w-[110px] text-center">Entrada</TableHead>
						<TableHead className="w-[200px] text-right">Operações</TableHead>
						<TableHead className="w-[120px]">Tecnico</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : repairs.length > 0 ? (
					<TableBody>
						{repairs.map((repair) => {
							const rowKey = repair.id ?? repair.repairNumber ?? "repair-row";
							const clientLabel = buildClientLabel(repair.client);
							const isComplaint = Boolean(repair.complaint);
							const isApproved = repair.quotationApproved === true;
							const isRejected = repair.quotationApproved === false;

							return (
								<TableRow
									key={rowKey}
									className={cn(
										"transition-colors",
										isComplaint ? "is-complaint" : "",
									)}
								>
									<TableCell className="font-medium text-foreground">
										<span className="tp-RepairsList-approved">
											{isApproved ? (
												<span
													title="Orçamento aprovado"
													className="tp-RepairsList-approved__circle"
												/>
											) : null}
											{isRejected ? (
												<span
													title="Orçamento recusado"
													className="tp-RepairsList-rejected__circle"
												/>
											) : null}
											{repair.repairNumber ?? "-"}
										</span>
									</TableCell>
									<TableCell className="text-center">
										{repair.storeRepairIdentifier ?? "-"}
									</TableCell>
									<TableCell className="text-center">
										{repair.storeBox ?? "-"}
									</TableCell>
									<TableCell>
										{buildEquipmentLabel(repair.clientEquipment)}
									</TableCell>
									<TableCell>
										{repair.client && onClientClick ? (
											<button
												type="button"
												className="text-primary underline-offset-2 hover:underline"
												onClick={() => onClientClick(repair.client ?? {})}
											>
												{clientLabel}
											</button>
										) : (
											clientLabel
										)}
									</TableCell>
									{showState ? (
										<TableCell>{getRepairStateLabel(repair.state)}</TableCell>
									) : null}
									<TableCell className="text-center">
										{resolveProblemLabel(repair)}
									</TableCell>
									<TableCell className="text-center">
										{formatDuration(repair.createdAt)}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onView(repair)}
											>
												Ver
											</Button>
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onChangeTechnician(repair)}
												disabled={loading || !canChangeTechnician(repair.state)}
											>
												Alterar Técnico
											</Button>
										</div>
									</TableCell>
									<TableCell>{repair.technician?.name ?? "-"}</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				) : (
					renderEmpty()
				)}
			</Table>
		</div>
	);
}
